import { LitElement, html } from 'lit';
import { API_ENDPOINT } from './constants';
import JSZip from 'jszip';
import ModelMeAIAnalytics from '../service/ModelMeAiAnalyticsService';
import '../components/AuthPrompt';
import '../components/WelcomeModal';

const isMobile = (window.visualViewport?.width || 0) <= 600;

function formatGarmentCategory(input: string) {
  return input
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('/');
}

function formatGarmentLabel(input: string) {
  return input
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

enum GarmentOutfitSegment {
  TOP = 'top',
  BOTTOM = 'bottom',
  FULL = 'full',
  //WAIST = 'waist', // TODO: implement handling for this
}

function makeGarment(frag: string, negFrag: string, seg: GarmentOutfitSegment = GarmentOutfitSegment.FULL) {
  const id = frag.toLowerCase().replace(/ /g, '-');
  return [id, frag, negFrag, seg];
}

// Expanded and balanced garment categories
const garmentCategories = {
  workout: [
    makeGarment('yoga pants', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('yoga shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('workout bra', '', GarmentOutfitSegment.TOP),
    makeGarment('tank top', '', GarmentOutfitSegment.TOP),
    makeGarment('compression leggings', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('hooded sweatshirt', '', GarmentOutfitSegment.TOP),
    makeGarment('running shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('sports crop top', '', GarmentOutfitSegment.TOP),
    makeGarment('athleisure jumpsuit', ''),
  ],
  beach_swim: [
    makeGarment('bikini', ''),
    makeGarment('one-piece swimsuit', ''),
    makeGarment('sarong', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('triangle bikini top', '', GarmentOutfitSegment.TOP),
    makeGarment('high-waisted bikini bottom', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('cut-out swimsuit', ''),
    makeGarment('rash guard', '', GarmentOutfitSegment.TOP),
    makeGarment('board shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('crochet beach cover-up', ''),
  ],
  sexy: [
    makeGarment('lingerie bodysuit', ''),
    makeGarment('corset', '', GarmentOutfitSegment.TOP),
    makeGarment('thong', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('lace bra', '', GarmentOutfitSegment.TOP),
    makeGarment('fishnet bodysuit', ''),
    makeGarment('silk chemise', ''),
    //makeGarment('garter belt', GarmentOutfitSegment.WAIST),
    makeGarment('sheer robe', ''),
    makeGarment('babydoll dress', ''),
    makeGarment('satin panties', '', GarmentOutfitSegment.BOTTOM),
  ],
  /* TODO: paywall this
  fetish: [
    makeGarment('leather harness'),
    makeGarment('latex bodysuit'),
    makeGarment('strappy bra', GarmentOutfitSegment.TOP),
    makeGarment('leather micro shorts', GarmentOutfitSegment.BOTTOM),
    makeGarment('vinyl corset', GarmentOutfitSegment.TOP),
    makeGarment('fishnet stockings', GarmentOutfitSegment.BOTTOM),
    makeGarment('choker harness', GarmentOutfitSegment.TOP),
    makeGarment('PVC skirt', GarmentOutfitSegment.BOTTOM),
  ],
  */
  casual: [
    makeGarment('jeans', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('crop top', '', GarmentOutfitSegment.TOP),
    makeGarment('t-shirt', '', GarmentOutfitSegment.TOP),
    makeGarment('denim shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('hoodie', '', GarmentOutfitSegment.TOP),
    makeGarment('maxi skirt', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('oversized sweater', '', GarmentOutfitSegment.TOP),
    makeGarment('joggers', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('casual sundress', '', GarmentOutfitSegment.FULL),
  ],
  shapewear: [
    makeGarment('waist trainer', '', GarmentOutfitSegment.TOP),
    makeGarment('bodysuit shapewear', ''),
    makeGarment('control shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('shaping camisole', '', GarmentOutfitSegment.TOP),
    makeGarment('high-waist leggings', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('butt-lifting shorts', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('tummy control swimsuit', ''),
    makeGarment('compression top', '', GarmentOutfitSegment.TOP),
    makeGarment('full-body shaper', ''),
  ],
  elegant: [
    makeGarment('evening gown', ''),
    makeGarment('cocktail dress', ''),
    makeGarment('silk blouse', '', GarmentOutfitSegment.TOP),
    makeGarment('tailored trousers', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('off-shoulder dress', ''),
    makeGarment('satin skirt', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('velvet blazer', '', GarmentOutfitSegment.TOP),
    makeGarment('sequin top', '', GarmentOutfitSegment.TOP),
    makeGarment('midi wrap dress', ''),
  ],
  streetwear: [
    makeGarment('graphic tee', '', GarmentOutfitSegment.TOP),
    makeGarment('cargo pants', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('bomber jacket', '', GarmentOutfitSegment.TOP),
    makeGarment('ripped jeans', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('oversized hoodie', '', GarmentOutfitSegment.TOP),
    makeGarment('track pants', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('bucket hat crop top', '', GarmentOutfitSegment.TOP),
    makeGarment('denim overalls', '', GarmentOutfitSegment.FULL),
    makeGarment('sneaker skirt', '', GarmentOutfitSegment.BOTTOM),
  ],
  professional: [
    makeGarment('blazer', '', GarmentOutfitSegment.TOP),
    makeGarment('pencil skirt', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('button-up shirt', '', GarmentOutfitSegment.TOP),
    makeGarment('slacks', '', GarmentOutfitSegment.BOTTOM),
    makeGarment('sheath dress', ''),
    makeGarment('tailored vest', '', GarmentOutfitSegment.TOP),
    makeGarment('pants suit', ''),
    makeGarment('jumpsuit', '', GarmentOutfitSegment.FULL),
  ],
};

// Convert category items to garment objects
const garmentsByCategory: Record<string, { id: number; label: string; promptFragment: string; negativePromptFragment: string; category: string; image_url: string; outfitSegment: string; }[]> = {};
Object.entries(garmentCategories).forEach(([category, items], catIdx) => {
  garmentsByCategory[category] = items.map(([label, promptFragment, negativePromptFragment, outfitSegment], i) => ({
    id: catIdx * 100 + i, // unique ID
    label,
    promptFragment,
    negativePromptFragment,
    category,
    image_url: `/static/images/garments/${label}.png`,
    outfitSegment,
  }));
});

// A static list of "premade" characters
const premadeCharacters = [
  {
    id: 'p-1',
    label: 'Scarlett',
    gender: 'female',
  },
  {
    id: 'p-2',
    label: 'Sia',
    gender: 'female',
  },
  {
    id: 'p-3',
    label: 'Lua Li',
    gender: 'female',
  },
  {
    id: 'p-4',
    label: 'Maria',
    gender: 'female',
  },
  {
    id: 'p-5',
    label: 'Hailey',
    gender: 'female',
  },
];

const premadeCharacterImages = {
  'p-1': [
    '/static/images/characters/sophia1.png',
    '/static/images/characters/sophia2.png'
  ],
  'p-2': [
    '/static/images/characters/anya1.png'
  ],
  'p-5': [
    '/static/images/characters/hailey/hailey-1.png'
  ],
};

// A list of recognized named colors for clothing
const NAMED_COLORS = [
  "white", "black", "red", "blue", "green", "yellow", "purple", "pink",
  "brown", "gray", "grey", "beige", "navy", "maroon", "teal", "turquoise",
  "coral colored", "ivory", "lavender purple", "orange", "silver", "gold", "khaki",
  "olive green", "magenta", "lime green", "indigo", "plum", "tan", "chocolate brown"
];

// Sample function to filter color list by search
function filterColors(search: string) {
  const term = search.trim().toLowerCase();
  if (!term) return NAMED_COLORS;
  return NAMED_COLORS.filter(c => c.toLowerCase().includes(term));
}

function filterGarmentsBySearch(garments, searchTerm: string) {
  // if searchTerm is empty => return all as-is
  if (!searchTerm.trim()) return garments;

  const lower = searchTerm.toLowerCase();
  const newMap = {};

  for (const [cat, items] of Object.entries(garments)) {
    const matched = items.filter((it: any) =>
      it.label.toLowerCase().includes(lower) ||
      it.promptFragment.toLowerCase().includes(lower)
    );
    if (matched.length > 0) {
      newMap[cat] = matched;
    }
  }
  return newMap;
}

function isGarmentEligibleForSelection(
  g,
  selectedGarments
) {
  // If user already selected a garment with outfitSegment=FULL,
  // no more garments can be chosen
  const hasFull = selectedGarments.some(sg => sg.outfitSegment === 'full');
  if (hasFull) {
    // The only item that can be selected is none
    // because we've locked in a full garment
    return false;
  }

  // If user has a top selected, we can't pick another top or a full
  const hasTop = selectedGarments.some(sg => sg.outfitSegment === 'top');
  // If user has a bottom selected, we can't pick another bottom or a full
  const hasBottom = selectedGarments.some(sg => sg.outfitSegment === 'bottom');

  // The new garment's segment
  const seg = g.outfitSegment;

  // If we already have a top, we can’t pick a new top or full
  if (hasTop && (seg === 'top' || seg === 'full')) {
    return false;
  }
  // If we already have a bottom, we can't pick a new bottom or full
  if (hasBottom && (seg === 'bottom' || seg === 'full')) {
    return false;
  }

  // Otherwise, garment is eligible
  return true;
}

export class DressingRoom extends LitElement {
  createRenderRoot() {
    // Return `this` so Tailwind utility classes in the main document apply.
    return this;
  }

  static properties = {
    characters: { type: Array },
    selectedCharacter: { type: Object },
    selectedCharacterId: { type: String },
    selectedGarments: { type: Array },
    previewImage: { type: String },
    loading: { type: Boolean },
    characterImages: { type: Array },
    selectedImageId: { type: Number },
    upscale: { type: Boolean },
    openCustom: { type: Boolean },
    customGarments: { type: Array },
    newGarmentPrompt: { type: String },
    newGarmentSegment: { type: GarmentOutfitSegment },
    generations: { type: Array },
    showNoPlanModal: { type: Boolean },
    showUpgradeModal: { type: Boolean },
    garments: { type: Array },
    // to control initial fade on preview/garment sections
    sectionsLocked: { type: Boolean },
    strength: { type: Number }, // new slider value
    activePreviewTab: { type: String }, // 'source' or 'generated'
    maskImage: { type: String },        // base64 data for mask
    overlayMask: { type: Boolean },     // show/hide mask overlay
    colorModalOpenForGarmentId: { type: Number },  // which garment has the modal open
    colorSearch: { type: String },
    searchTerm: { type: String },
    modelSearchTerm: { type: String },
    openCategories: { type: Object },
    showCustomForm: { type: Boolean },

    positivePrompt: { type: String },
    negativePrompt: { type: String },

    tourStep: { type: Number },
    showModelModal: { type: Boolean },
    generateIdToIsPositiveRating: { type: Object },

    showAuthPrompt: { type: Boolean },
    isSignedIn: { type: Boolean },

    newModelName: { type: String },
    newModelFiles: { type: Array },

    isNude: { type: Boolean },
  };

  constructor() {
    super();
    this.characters = [];
    this.selectedCharacter = null;
    this.selectedGarments = [];
    this.previewImage = '';
    this.loading = false;
    this.characterImages = [];
    this.selectedImageId = null;
    this.upscale = false;
    this.openCategories = {};
    Object.keys(garmentsByCategory).forEach((cat) => {
      this.openCategories[cat] = false;
    });
    this.openCustom = false;
    this.customGarments = [];
    this.newGarmentPrompt = '';
    this.newGarmentSegment = GarmentOutfitSegment.FULL;
    this.generations = [];
    this.showNoPlanModal = false;
    this.showUpgradeModal = false;
    this.garments = [];
    // lock sections at first
    this.sectionsLocked = true;

    this.strength = 50;        // default
    this.activePreviewTab = 'source'; // default to "Source Image"
    this.maskImage = '';
    this.overlayMask = false;

    this.colorModalOpenForGarmentId = null;
    this.colorSearch = '';

    this.searchTerm = '';
    this.modelSearchTerm = '';
    this.showCustomForm = false;
    // new textareas
    this.positivePrompt = '';
    this.negativePrompt = '';

    this.tourStep = 1;

    this.showModelModal = false;
    this.allCharacterImages = {};

    this.generateIdToIsPositiveRating = {};

    this.showAuthPrompt = false;
    this.isSignedIn = false;

    this.newModelName = ''
    this.newModelFiles = []

    this.isNude = false;

    this.checkAuthStatus();

    // Check for ?charId= in the URL, to auto-select a character
    const params = new URLSearchParams(window.location.search);
    const preselectedCharId = params.get('charId');

    this.restoreStateFromLocalStorage();

    // Load user-created characters
    this.fetchCharacters().then(async (chars) => {
      if (preselectedCharId) {
        const found = this.characters.find((c) => String(c.id) === String(preselectedCharId));
        if (found) {
          this.selectCharacter(found);
        }
      }

      this.newModelName = `Model #${chars.length + 1}`;

      const characterToImgs = {}
      await Promise.all(chars.map(c => this.fetchCharacterImages(c.id).then(imgs => characterToImgs[c.id] = imgs)))
      this.allCharacterImages = characterToImgs
      this.requestUpdate();
    });

    this.fetchCustomGarments();
    this.initIndexedDB();

    ModelMeAIAnalytics.getPlausible()
  }

  async checkAuthStatus() {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/me`, { credentials: 'include' });
      this.isSignedIn = res.ok;
    } catch (err) {
      this.isSignedIn = false;
    }
  }

  toggleModelModal(open = !this.showModelModal) {
    if (!this.isSignedIn) {
      this.showAuthPrompt = true;
      return;
    }

    ModelMeAIAnalytics.getPlausible()?.trackEvent('toggle_model_modal', { props: { open } })

    this.showModelModal = open;
    if (open) {
      document.body.classList.add('overflow-y-hidden')
    } else {
      document.body.classList.remove('overflow-y-hidden')
    }
  }

  computeTourStep() {
    // If user has selected a model
    if (!this.selectedCharacter) {
      this.tourStep = 1;
      return;
    }
    // If user has selected no garments
    if (this.selectedGarments.length === 0) {
      this.tourStep = 2;
      return;
    }
    // If user hasn't generated yet => step 3
    // (we can track if user has generated at least once or not
    // but for now let's say if we have no “generatedImage” => step 3)
    if (!this.generatedImage) {
      this.tourStep = 3;
      return;
    }
    // otherwise, no more steps
    this.tourStep = 0; // or null => means no highlight
  }

  saveStateToLocalStorage() {
    const state = {
      characterId: this.selectedCharacter ? String(this.selectedCharacter.id) : '',
      imageId: this.selectedImageId ?? null,
      garments: this.selectedGarments.map((g) => g.id),
    };
    localStorage.setItem('dressingRoomState', JSON.stringify(state));
  }

  restoreStateFromLocalStorage() {
    const raw = localStorage.getItem('dressingRoomState');
    if (!raw) return;
    try {
      const st = JSON.parse(raw);
      if (st.characterId) {
        this.selectedCharacterId = st.characterId;
      }
      if (typeof st.imageId === 'number') {
        this.selectedImageId = st.imageId;
      }
      if (Array.isArray(st.garments)) {
        this.pendingGarmentIds = st.garments;
      }
    } catch (err) {
      console.error('Failed to parse dressing room state:', err);
    }
  }

  matchPendingGarments() {
    if (!this.pendingGarmentIds || !this.garments.length) return;
    const matched = [];
    for (const gId of this.pendingGarmentIds) {
      const found = this.garments.find((g) => g.id === gId);
      if (found) matched.push(found);
    }
    this.selectedGarments = matched;
    delete this.pendingGarmentIds;
  }

  initIndexedDB() {
    const request = window.indexedDB.open('ClothingAI_DB', 1);

    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('generations')) {
        db.createObjectStore('generations', {
          keyPath: 'id',
          autoIncrement: true,
        });
      }
    };

    request.onsuccess = (e) => {
      this.db = e.target.result;
      this.loadGenerationsFromIDB();
    };

    request.onerror = (e) => {
      console.error('IndexedDB error:', e.target.error);
    };
  }

  async loadGenerationsFromIDB() {
    if (!this.db) return;
    const tx = this.db.transaction('generations', 'readonly');
    const store = tx.objectStore('generations');

    const request = store.getAll();
    request.onsuccess = () => {
      this.generations = request.result.sort((a, b) => b.createdAt - a.createdAt);
    };
    request.onerror = () => {
      console.error('Failed to load generations');
    };
  }

  async storeGenerationInIDB({ characterId, characterImageId, garmentPrompts, genImageBlob, maskImageBlob }) {
    if (!this.db) return;
    const tx = this.db.transaction('generations', 'readwrite');
    const store = tx.objectStore('generations');

    const record = {
      characterId,
      characterImageId,
      garmentPrompts,
      imageBlob: genImageBlob,
      maskImageBlob,
      createdAt: Date.now(),
    };

    store.add(record).onsuccess = () => {
      this.loadGenerationsFromIDB();
    };
  }

  async fetchCharacters() {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/characters`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        this.characters = data;
        return data
      }
    } catch (err) {
      console.error('Error fetching characters:', err);
    }

    return []
  }

  async fetchCustomGarments() {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/customgarments`, { credentials: 'include' });
      if (res.ok) {
        this.customGarments = await res.json();
      }
    } catch (err) {
      console.error('Error fetching custom garments:', err);
      this.customGarments = [];
    }
  }

  async fetchCharacterImages(charId: string) {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/characters/${charId}/images`, {
        credentials: 'include',
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.error('Error fetching character images:', err);
    }
    return null
  }

  async loadCharacterImages(charId) {
    this.characterImages = await this.fetchCharacterImages(charId)
  }

  selectCharacter(character) {
    ModelMeAIAnalytics.getPlausible()?.trackEvent('select_character', { props: { character } })
    document.getElementById('model-dropdown')?.removeAttribute('open')
    this.selectedCharacter = character;
    this.selectedGarments = [];
    this.previewImage = '';
    this.characterImages = [];

    // Unlock the other sections once a character is selected
    this.sectionsLocked = false;

    const characterIsPremade = String(character.id).startsWith('p-');
    if (characterIsPremade) {
      this.characterImages = premadeCharacterImages[character.id].map((url, i) => ({
        id: i,
        image_url: url,
      }));
      if (this.characterImages[0]) {
        this.previewImage = this.characterImages[0].image_url;
      }
    } else {
      this.loadCharacterImages(character.id).then(() => {
        if (this.characterImages.length > 0) {
          this.selectedImageId = this.characterImages[0].id;
          this.previewImage = this.characterImages[0].image_url;
        }
      });
    }
    this.saveStateToLocalStorage();
    this.computeTourStep();
  }

  get filteredGarmentsByCategory() {
    const term = this.searchTerm.trim().toLowerCase();
    const result = {};
    for (const [cat, items] of Object.entries(garmentsByCategory)) {
      result[cat] = items.filter(
        (g) =>
          g.label.toLowerCase().includes(term) ||
          g.promptFragment.toLowerCase().includes(term)
      );
    }
    return result;
  }

  get filteredCustomGarments() {
    const term = this.searchTerm.trim().toLowerCase();
    return this.customGarments.filter(
      (g) =>
        g.label?.toLowerCase().includes(term) ||
        g.prompt_fragment?.toLowerCase().includes(term)
    ).map(g => ({
      id: g.id,
      label: g.label,
      promptFragment: g.prompt_fragment,
      category: g.label,
      outfitSegment: g.outfit_segment,
    }));
  }

  toggleGarment(newGarment) {
    if (this.isGarmentSelected(newGarment)) {
      this.selectedGarments = this.selectedGarments.filter(g => g.id !== newGarment.id);
      this.computeTourStep();
      this.requestUpdate();
      return;
    }

    // Determine if newGarment.outfitSegment is "top", "bottom", or "full"
    const newSeg = newGarment.outfitSegment;

    console.log(newGarment)
    // Build a quick map of what's already selected
    const existingSegments = new Set(this.selectedGarments.map(g => g.outfitSegment));

    // If user is picking "full" but we have any top/bottom, we must wipe them out
    // and set only the new garment.
    if (newSeg === GarmentOutfitSegment.FULL) {
      // Overwrite
      this.selectedGarments = [newGarment];
      this.computeTourStep();
      this.requestUpdate();
      return;
    }

    // If user picks "top" but we currently have "full", we remove "full" and add "top"
    if (newSeg === 'top' && existingSegments.has(GarmentOutfitSegment.FULL)) {
      this.selectedGarments = [newGarment];
      this.computeTourStep();
      this.requestUpdate();
      return;
    }

    // If user picks "top" but we already have a different top => remove old top, add new
    if (newSeg === GarmentOutfitSegment.TOP) {
      // remove any top garment from selectedGarments
      this.selectedGarments = this.selectedGarments.filter(g => g.outfitSegment !== GarmentOutfitSegment.TOP);
      // then add the new top
      this.selectedGarments.push(newGarment);
      this.requestUpdate();
      this.computeTourStep();
      return;
    }

    // Similar for "bottom"
    if (newSeg === GarmentOutfitSegment.BOTTOM) {
      // if we have "full", overwrite
      if (existingSegments.has(GarmentOutfitSegment.FULL)) {
        this.selectedGarments = [newGarment];
        this.requestUpdate();
        this.computeTourStep();
        return;
      }
      // remove any existing "bottom" from selected
      this.selectedGarments = this.selectedGarments.filter(g => g.outfitSegment !== GarmentOutfitSegment.BOTTOM);
      this.selectedGarments.push(newGarment);
      this.requestUpdate();
      this.computeTourStep();

      return;
    }
  }

  isGarmentSelected(garment) {
    return this.selectedGarments.some(
      (g) => g.id === garment.id && g.category === garment.category
    );
  }

  toggleCategory(cat) {
    this.openCategories[cat] = !this.openCategories[cat];
    this.requestUpdate();
  }

  toggleCustomSection() {
    this.openCustom = !this.openCustom;
  }

  toggleCustomForm() {
    this.showCustomForm = !this.showCustomForm;
    if (!this.showCustomForm) {
      this.newGarmentPrompt = '';
      this.newGarmentSegment = GarmentOutfitSegment.FULL;
    }
  }

  // Updated to remove label input and store garment with the same value for label & prompt
  async createCustomGarment() {
    if (!this.newGarmentPrompt.trim()) {
      alert('Please provide a prompt description for your garment.');
      return;
    }
    try {
      const res = await fetch(`${API_ENDPOINT}/api/customgarments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: this.newGarmentPrompt,
          prompt_fragment: this.newGarmentPrompt,
          outfit_segment: this.newGarmentSegment,
        }),
      });
      if (res.ok) {
        const created = await res.json();
        this.customGarments = [...this.customGarments, created];
        this.toggleCustomForm();
      } else {
        alert('Failed to create custom garment');
      }
    } catch (err) {
      console.error('Error creating custom garment:', err);
    }
  }

  selectImage(imageId) {
    this.selectedImageId = imageId;
    const found = this.characterImages.find((ci) => ci.id === imageId);
    if (found) {
      this.previewImage = found.image_url;
    }
  }

  async flagImageBad() {
    document.getElementById('user_guide_modal')?.showModal()

    try {
      let imageBlob = null;

      if (this.previewImage) {
        const response = await fetch(this.generatedImage);
        imageBlob = await response.blob();
      }

      const formData = new FormData();
      formData.append("character_id", this.selectedCharacter?.id || "");
      this.selectedGarments.forEach((g, index) =>
        formData.append(`garment_prompts[${index}]`, g.color) // Adjust as needed
      );
      formData.append("upscale", this.upscale ? "true" : "false");
      formData.append("is_nude", this.isNude ? "true" : "false");
      formData.append("source_image_url", this.previewImage || "");

      if (imageBlob) {
        formData.append("raw_image", imageBlob, "image.jpg"); // Adjust filename if needed
      }

      const res = await fetch(`${API_ENDPOINT}/api/flagimage`, {
        method: "POST",
        credentials: "include",
        body: formData, // FormData auto-sets correct content type
      });

      if (res.ok) {
        alert('Image flagged successfully. Thank you!');
      } else {
        alert('Failed to flag image');
      }

      this.generateIdToIsPositiveRating[this.generatedImage] = false
    } catch (err) {
      console.error('Error flagging image:', err);
      alert('Error flagging image');
    }
  }

  flagImageGood() {
    this.generateIdToIsPositiveRating[this.generatedImage] = true
  }

  async handleGenerateImage() {
    if (this.loading) {
      return
    }

    if (!this.selectedCharacter) return;
    this.loading = true;

    const garmentPrompts = this.selectedGarments.map((g) => {
      const gt = g.outfitSegment === GarmentOutfitSegment.TOP ? 'top' : g.outfitSegment === GarmentOutfitSegment.BOTTOM ? 'bottom' : 'top'
      return {
        garment: g.promptFragment,
        garment_color: g.color,
        garment_type: gt,
      }
    });
    const negativeGarmentPrompts = this.selectedGarments.map((g) => {
      const gt = g.outfitSegment === GarmentOutfitSegment.TOP ? 'top' : g.outfitSegment === GarmentOutfitSegment.BOTTOM ? 'bottom' : 'top'
      return {
        garment: g.negativePromptFragment,
        garment_color: g.color,
        garment_type: gt,
      }
    });
    const characterImageId = this.selectedImageId ?? 0;

    const payload = {
      character_id: this.selectedCharacter.id,
      garment_prompts: garmentPrompts,
      negative_garment_prompts: negativeGarmentPrompts,
      character_image_id: characterImageId,
      upscale: this.upscale,
      is_nude: this.isNude,
      positive_prompt: this.positivePrompt,
      negative_prompt: this.negativePrompt,
      //strength: this.strength,
    }

    ModelMeAIAnalytics.getPlausible()?.trackEvent('generate_image', { props: { payload } });

    try {
      const res = await fetch(`${API_ENDPOINT}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        if (res.status === 402) {
          const data = await res.json();
          if (data.reason === 'NO_PLAN') {
            this.showNoPlanModal = true;
          }
        } else if (res.status === 429) {
          const data = await res.json();
          if (data.reason === 'LIMIT_REACHED') {
            this.showUpgradeModal = true;
          }
        }
        this.loading = false;
        return;
      }

      const data = await res.json();
      const jobId = data.job_id;
      if (!jobId) {
        this.loading = false;
        alert('No job_id returned');
        return;
      }

      this.pollGeneration(jobId, {
        characterId: this.selectedCharacter.id,
        characterImageId,
        garmentPrompts,
      });
    } catch (err) {
      console.error('Error generating image:', err);
      this.loading = false;
    }
  }

  pollGeneration(jobId, { characterId, characterImageId, garmentPrompts }) {
    let attempts = 0;
    const maxAttempts = 500;
    const intervalMs = 5000;

    const intervalId = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_ENDPOINT}/api/generate/status/${jobId}`, {
          credentials: 'include',
        });

        if (res.ok) {
          const blob = await res.blob();
          if (blob?.size) {
            const zip = await JSZip.loadAsync(blob);
            const genImageBlob = await zip.file("gen.png")?.async("blob");
            const maskImageBlob = await zip.file("mask.png")?.async("blob");

            if (genImageBlob.size > 0) {
              this.previewImage = URL.createObjectURL(genImageBlob);
              this.generatedImage = URL.createObjectURL(genImageBlob);
              this.activePreviewTab = 'generated';

              clearInterval(intervalId);

              this.loading = false;

              // Store generation in IndexedDB
              this.storeGenerationInIDB({
                characterId,
                characterImageId,
                garmentPrompts,
                genImageBlob,
                maskImageBlob,
              });
            }

            if (maskImageBlob.size > 0) {
              this.maskImage = URL.createObjectURL(maskImageBlob);
            }

            this.computeTourStep();
          }
        } else {
          console.warn('Polling not ready, status:', res.status);
        }
      } catch (pollErr) {
        console.error('Error polling generation status:', pollErr);
      }

      if (attempts >= maxAttempts) {
        console.error('Reached max attempts for generation polling');
        this.loading = false;
        clearInterval(intervalId);
      }
    }, intervalMs);
  }

  goToCreateCharacter() {
    // Unlock sections if user wants to create a new character
    this.sectionsLocked = false;
    // Navigate
    window.history.pushState({}, '', '/character-editor');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  exportImage() {
    if (this.previewImage) {
      window.open(this.previewImage, '_blank');
    }
  }

  async downloadSingleGeneration(gen) {
    const url = URL.createObjectURL(gen.imageBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gen_${gen.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async downloadAllGenerations() {
    const zip = new JSZip();
    let count = 0;
    for (const gen of this.generations) {
      const arrayBuffer = await gen.imageBlob.arrayBuffer();
      zip.file(`gen_${gen.id}.png`, arrayBuffer);
      count++;
    }
    if (count === 0) {
      alert('No generations to download.');
      return;
    }
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `all_generations.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  loadGenerationWorkspace(gen) {
    if (!confirm('Load this generation workspace? This will overwrite your current selection.')) {
      return;
    }

    const foundChar = this.characters.find((c) => c.id === gen.characterId);
    if (foundChar) {
      this.selectedCharacter = foundChar;
      this.characterImages = [];
      this.loadCharacterImages(foundChar.id).then(() => {
        this.selectedImageId = gen.characterImageId;
        const foundImg = this.characterImages.find((ci) => ci.id === gen.characterImageId);
        if (foundImg) {
          this.previewImage = foundImg.image_url;
        } else {
          this.previewImage = URL.createObjectURL(gen.imageBlob);
        }

      });
    } else {
      this.selectedCharacter = null;
      this.previewImage = URL.createObjectURL(gen.imageBlob);
    }

    this.generatedImage = URL.createObjectURL(gen.imageBlob);

    // Re-select garments
    const mapped = [];
    gen.garmentPrompts.forEach(prompt => {
      const { garment, garment_color, garment_type } = prompt
      let foundGarment = null;
      Object.entries(garmentsByCategory).some(([category, items]) => items.some(it => {
        if (it.promptFragment === garment) {
          foundGarment = it
          return true
        }
      }));
      if (foundGarment) {
        mapped.push({
          ...foundGarment,
          color: garment_color,
        });
      }
    })

    this.selectedGarments = mapped;

    this.activePreviewTab = 'generated';
  }

  toggleMaskOverlay(e) {
    this.overlayMask = e.target.checked;
  }

  openColorModal(garmentId: number) {
    this.colorModalOpenForGarmentId = garmentId;
    this.colorSearch = ''; // reset search
  }

  closeColorModal() {
    this.colorModalOpenForGarmentId = null;
  }

  /** Called when user picks a color from the list or enters a custom color. */
  setGarmentColor(garmentId: number, color: string) {
    // find that garment in selectedGarments
    const i = this.selectedGarments.findIndex(g => g.id === garmentId);
    if (i >= 0) {
      this.selectedGarments[i].color = color; // store named color
    }
    this.closeColorModal();
  }

  async deleteCustomGarment(g) {
    if (!confirm(`Delete custom garment "${g.label}"? This cannot be undone.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_ENDPOINT}/api/customgarments/${g.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        // remove from local list
        this.customGarments = this.customGarments.filter(cg => cg.id !== g.id);
        // if it was selected, remove from selectedGarments
        this.selectedGarments = this.selectedGarments.filter(sg => sg.id !== g.id);
      } else {
        alert('Failed to delete garment');
      }
    } catch (err) {
      console.error('Error deleting garment:', err);
      alert('Error deleting garment');
    } finally {
      this.contextMenuOpenForGarmentId = null;
    }
  }

  openEditCustomGarment(g) {
    // set editing item and show edit form
    this.editingCustomGarment = { ...g }; // copy
    this.showEditForm = true;
    this.contextMenuOpenForGarmentId = null;
    this.requestUpdate()
  }

  async updateCustomGarment() {
    const g = this.editingCustomGarment;
    if (!g.promptFragment.trim()) {
      alert('Prompt fragment is required.');
      return;
    }
    // call PUT /api/customgarments/:id
    try {
      const res = await fetch(`${API_ENDPOINT}/api/customgarments/${g.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: g.label,
          prompt_fragment: g.promptFragment,
          outfit_segment: g.outfitSegment,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        // update the local customGarments array
        this.customGarments = this.customGarments.map(cg =>
          cg.id === g.id ? updated : cg
        );
        this.showEditForm = false;
        this.editingCustomGarment = null;
      } else {
        alert('Failed to update garment');
      }
    } catch (err) {
      console.error('Error updating garment:', err);
      alert('Error updating garment');
    }
    this.requestUpdate()
  }

  cancelEditCustomGarment() {
    this.showEditForm = false;
    this.editingCustomGarment = null;
    this.requestUpdate()
  }

  toggleContextMenuFor(garmentId: number, e: MouseEvent) {
    e.stopPropagation();
    if (this.contextMenuOpenForGarmentId === garmentId) {
      this.contextMenuOpenForGarmentId = null;
    } else {
      this.contextMenuOpenForGarmentId = garmentId;
    }
    this.requestUpdate();
  }

  handleNewModelFilesSelected(e) {
    this.newModelFiles = Array.from(e.target.files);
  }

  async createNewModel() {
    if (!this.isSignedIn) {
      this.showAuthPrompt = true;
      return;
    }

    if (!this.newModelName.trim()) {
      alert('Please provide a model name');
      return;
    }
    if (this.newModelFiles.length === 0) {
      alert('Please upload at least one image');
      return;
    }

    this.loading = true;
    try {
      // Create character
      const res = await fetch(`${API_ENDPOINT}/api/characters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: this.newModelName,
          gender: 'female',
          physical_attrs: [],
        }),
      });

      if (!res.ok) throw new Error('Failed to create character');
      const data = await res.json();
      const characterId = data.id;

      // Upload images
      const formData = new FormData();
      this.newModelFiles.forEach(file => formData.append('images', file));
      const uploadRes = await fetch(`${API_ENDPOINT}/api/characters/${characterId}/images/batch`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!uploadRes.ok) throw new Error('Failed to upload images');

      // Refresh characters and select the new one
      await this.fetchCharacters();
      const newCharacter = this.characters.find(c => c.id === characterId);
      if (newCharacter) {
        this.selectCharacter(newCharacter);
        this.newModelName = `Model ${this.characters.length + 1}`; // Update default name
        this.newModelFiles = [];
      }
    } catch (err) {
      console.error('Error creating new model:', err);
      alert(err.message);
    } finally {
      this.loading = false;
    }
  }

  renderModelSelector() {
    const highlight = (this.tourStep === 1) ? 'animate-pulseShadow' : '';

    return html`
    <button
      class="btn btn-primary w-full justify-between text-white ${highlight}"
      @click=${() => this.toggleModelModal(true)}
    >
      ⭐ ${this.selectedCharacter?.label || 'Select Model'}
      <!-- Some small icon at the right -->
      <svg class="h-5 w-5 ml-2" fill="none" stroke="currentColor" stroke-width="2"
        viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round"
          d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  `;
  }

  renderModelModal() {
    if (!this.showModelModal) return null; // hidden if false

    // Filter by label
    const searchTerm = (this.modelSearchTerm || '').trim().toLowerCase();
    const filtered = this.characters.filter(m =>
      m.label.toLowerCase().includes(searchTerm)
    );

    return html`
    <div class="fixed inset-0 z-50 bg-[rgba(0,0,0,0.8)] flex items-center justify-center max-w-screen max-h-screen"
         @click=${() => this.toggleModelModal(false)}>
      <div class="bg-base-100 rounded shadow max-w-xl w-full h-auto sm:h-[80%] overflow-y-auto max-h-screen relative "
           @click=${(e: Event) => e.stopPropagation()}>
        <!-- Modal Header -->
        <div class="flex items-center justify-between mb-4 border-b pb-2 px-2">
          <h2 class="text-xl font-bold">Select Model</h2>
          <button class="btn btn-sm btn-circle"
                  @click=${() => this.toggleModelModal(false)}>
            ✕
          </button>
        </div>

        <div class="h-5/6 px-2">
        <!-- Grid of Models -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 px-2">
          ${filtered.map(model => {
      // pick or generate a first image
      let firstImg = '';
      // user-created => find the user's first image if available
      const imgs = this.allCharacterImages[model.id]
      if (imgs && imgs.length > 0) {
        firstImg = imgs[0].image_url
      }

      return html`
                      <div class="card bg-base-200 shadow p-2 flex flex-col items-center cursor-pointer" @click=${() => { this.selectCharacter(model); this.toggleModelModal(false); }}>
                        <img src="${firstImg}" class="w-24 h-24 object-cover object-top rounded mb-2" />
                        <h3 class="font-semibold mb-1">${model.label}</h3>
                        <button class="btn btn-primary btn-sm">
                          Select
                        </button>
                      </div>
                    `;
    })}
          </div>

        <span class="text-gray-300 w-full hidden">
          Get Started Quickly With Pre-Made Models
        </span>

        <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 pb-[90px] hidden">
          ${premadeCharacters.map(model => {
      let firstImg = '';
      const pmImgs = premadeCharacterImages[model.id] || [];
      if (pmImgs.length > 0) firstImg = pmImgs[0];

      return html`
                      <div class="card bg-base-200 shadow p-2 flex flex-col items-center cursor-pointer" @click=${() => { this.selectCharacter(model); this.toggleModelModal(false); }}>
                        <img src="${firstImg}" class="w-24 h-24 object-cover object-top rounded mb-2" />
                        <h3 class="font-semibold mb-1">${model.label}</h3>
                        <button class="btn btn-secondary btn-sm">
                          Select
                        </button>
                      </div>
                    `;
    })}
        </div>
        </div>

        <!-- Search input -->
        <div class="mb-4 ${this.characters.length === 0 ? 'hidden' : ''}">
          <input
            type="text"
            class="input input-bordered w-full"
            placeholder="Search models..."
            .value=${this.modelSearchTerm}
            @input=${(e: any) => this.modelSearchTerm = e.target.value}
          />
        </div>

        <div class="p-2 bg-primary absolute bottom-0 w-full rounded flex flex-col gap-2">
          <span class="font-bold ">
            Use Your Own Images:
          </span>
          <!-- "Create New Model" -->
          <button class="btn btn-outline w-full"
                  @click=${this.goToCreateCharacter}>
            + Create New Model
          </button>
        </div>

      </div>
    </div>
  `;
  }

  /** In the "Garments" section, we do a robust search with open/close logic. */
  get garmentsFilteredBySearch() {
    // If searchTerm is empty => return normal object
    // else open categories that have matches
    return filterGarmentsBySearch(garmentsByCategory, this.searchTerm);
  }

  /** If no category had any match, show "Make Custom Garment". */
  get foundAnyMatches() {
    let found = false;
    for (const [cat, items] of Object.entries(this.garmentsFilteredBySearch)) {
      if (items.length > 0) {
        found = true;
        break;
      }
    }
    return found;
  }

  renderGenerationsSection() {
    return html`
      <div class="">
        <h2 class="text-lg font-semibold mb-2 items-center justify-between">
          🖼️ Generations
        </h2>
        <button
          class="px-3 py-1 mb-1 border border-white text-white bg-transparent hover:bg-neutral-600 hover:text-white rounded text-sm cursor-pointer ${this.generations.length > 1 ? '' : 'hidden'}"
          @click=${this.downloadAllGenerations}
        >
          Download All
        </button>
      <div class="max-h-[80vh] overflow-y-auto">
        ${this.generations.length === 0
        ? html`<div class="text-gray-400 text-sm">No saved generations yet.</div>`
        : html`
              <div class="grid grid-cols-2 gap-2">
                ${this.generations.map(
          (gen) => html`
                    <div class="bg-gray-800 rounded p-2 relative">
                      <img
                        src=${URL.createObjectURL(gen.imageBlob)}
                        class="w-full h-auto cursor-pointer rounded mb-2"
                        @click=${() => this.loadGenerationWorkspace(gen)}
                      />
                      <button
                        class="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 p-1 rounded"
                        title="Download image"
                        @click=${(e) => {
              e.stopPropagation();
              this.downloadSingleGeneration(gen);
            }}
                      >
                        ⬇
                      </button>
                      <div class="text-xs text-gray-400">
                        ${this.characters.find((c) => c.id === gen.characterId)?.label}<br />
                        ${gen.garmentPrompts.map(g => (g.garment_color ? g.garment_color + ' ' : '') + g.garment).join(', ')}
                      </div>
                    </div>
                  `
        )}
              </div>
            `}
              </div>
      </div>
    `;
  }

  /** Renders the small gallery of character images below the main preview so user can choose a source image */
  renderCharacterGallery() {
    if (!this.selectedCharacter) return null;
    if (!this.characterImages || this.characterImages.length === 0) return null;
    return html`
      <div class="mt-4 flex gap-2 overflow-x-auto">
        ${this.characterImages.map(ci => {
      const isSelected = ci.id === this.selectedImageId;
      return html`
            <img
              src="${ci.image_url}"
              class="w-20 h-20 object-cover rounded cursor-pointer
                ${isSelected ? 'border-4 border-secondary' : 'border'}"
              @click=${() => this.selectImage(ci.id)}
            />
          `;
    })}
      </div>
    `;
  }

  renderPreviewTabs() {
    return this.generatedImage ? html`
      <div class="flex border-b border-gray-700 mt-4">
        <button
          class="
            flex-1 py-2 text-center
            ${this.activePreviewTab === 'source'
        ? 'border-b-2 border-pink-600 font-bold'
        : 'hover:bg-gray-700'}"
          @click=${() => (this.activePreviewTab = 'source')}
        >
          Source Image
        </button>
        <button
          class="
            flex-1 py-2 text-center
            ${this.activePreviewTab === 'generated'
        ? 'border-b-2 border-pink-600 font-bold'
        : 'hover:bg-gray-700'}"
          @click=${() => (this.activePreviewTab = 'generated')}
        >
          Generated
        </button>
      </div>
    ` : '';
  }

  renderPreviewSection() {
    // Decide which image to show:
    const sourceImg = this.characterImages.find((ci) => ci.id === this.selectedImageId)?.image_url;
    const showGenerated = this.activePreviewTab === 'generated' && Boolean(this.generatedImage);

    // If no generated image and no selected character, show cold-start UI
    if (!this.generatedImage && !this.selectedCharacter) {
      return html`
        <div class="card-body p-2">
          <h2 class="card-title text-xl font-semibold mb-4">🔎 Get Started</h2>
          
          <!-- Existing Models Grid -->
            ${this.characters.length > 0 ? html`
          <div class="mb-6">
            <h3 class="text-lg font-semibold mb-2">Select Existing Model</h3>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[40vh] overflow-y-auto">
              ${this.characters.map(model => {
        const imgs = this.allCharacterImages[model.id] || [];
        const firstImg = imgs.length > 0 ? imgs[0].image_url : '';
        return html`
                  <div class="card bg-base-200 shadow p-2 flex flex-col items-center cursor-pointer"
                       @click=${() => this.selectCharacter(model)}>
                    <img src="${firstImg}" class="w-24 h-24 object-cover object-top rounded mb-2"
                         alt="${model.label}" />
                    <h3 class="font-semibold mb-1">${model.label}</h3>
                    <button class="btn btn-primary btn-sm">Select</button>
                  </div>
                `;
      })}
            </div>
          </div>

          <!-- Create New Model Form -->
          <div class="divider">or</div>
            `: ''}
          <h3 class="text-lg font-semibold mb-2">Create New Model</h3>
          <div class="space-y-2">
            <div class="form-control">
              <label class="label">
                <span class="label-text font-semibold">Upload Images</span>
              </label>
              <input
                type="file"
                multiple
                @change=${this.handleNewModelFilesSelected}
                class="file-input file-input-bordered file-input-primary w-full text-sm"
                accept="image/*"
                ?disabled=${this.loading}
              />
              <div class="mt-2 text-sm text-gray-400">
                Tip: Use clear, full-body shots for best results
              </div>
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text text-xs font-semibold">Model Name</span>
              </label>
              <input
                type="text"
                class="input input-bordered w-full h-auto"
                .value=${this.newModelName}
                @input=${(e) => this.newModelName = e.target.value}
                ?disabled=${this.loading}
              />
            </div>
            <button
              class="btn btn-primary w-full ${this.loading ? 'loading' : ''}"
              @click=${this.createNewModel}
              ?disabled=${this.loading}
            >
              ${this.loading ? 'Creating...' : 'Create & Select Model'}
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="card-body p-2">
        <div class="flex items-center justify-between mb-4">
          <h2 class="card-title text-xl font-semibold">🔎 Preview</h2>
          ${Boolean(this.generatedImage) && this.generateIdToIsPositiveRating[this.generatedImage] === undefined ? html`
            <div class="flex gap-2 ">
              <label class="">
                What you expected?
              </label>
              <button class="btn btn-soft btn-error ${showGenerated ? '' : 'hidden'}" @click=${this.flagImageGood}>
                Yes
              </button>
              <button class="btn btn-soft btn-error ${showGenerated ? '' : 'hidden'}" @click=${this.flagImageBad}>
                No
              </button>
            </div>
          ` : ''}

        </div>

        <!-- Main preview container with relative positioning if overlaying mask -->
        <div class="card-body relative w-full flex items-center justify-center rounded">
          ${showGenerated
        ? html`
                <img class="max-w-full rounded" src="${this.generatedImage}" />
                <!-- if there's a mask and user wants overlay, position it absolutely -->
                ${this.maskImage && this.overlayMask
            ? html`
                      <img
                        class="absolute max-w-full mix-blend-screen pointer-events-none opacity-90"
                        src="${this.maskImage}"
                      />
                    `
            : ''}
              `
        : html`
                <!-- Otherwise show source image -->
                ${sourceImg
            ? html`<img class="max-w-full max-h-[400px] rounded" src="${sourceImg}" />`
            : ''}
              `}
        </div>

        <!-- Tabs to switch between source & generated -->
        ${this.renderPreviewTabs()}

        <!-- If we have a generated image, show share/export + overlay mask checkbox -->
        ${this.generatedImage
        ? html`
              <div class="flex items-center justify-between mt-3">
                <!-- Share/Export as icon button -->
                <button
                  class="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                  @click=${this.exportImage}
                >
                  <svg fill="white" height="16px" width="16px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
           viewBox="0 0 458.624 458.624" xml:space="preserve">
  <g>
          <g>
                  <path d="M339.588,314.529c-14.215,0-27.456,4.133-38.621,11.239l-112.682-78.67c1.809-6.315,2.798-12.976,2.798-19.871
                          c0-6.896-0.989-13.557-2.798-19.871l109.64-76.547c11.764,8.356,26.133,13.286,41.662,13.286c39.79,0,72.047-32.257,72.047-72.047
                          C411.634,32.258,379.378,0,339.588,0c-39.79,0-72.047,32.257-72.047,72.047c0,5.255,0.578,10.373,1.646,15.308l-112.424,78.491
                          c-10.974-6.759-23.892-10.666-37.727-10.666c-39.79,0-72.047,32.257-72.047,72.047s32.256,72.047,72.047,72.047
                          c13.834,0,26.753-3.907,37.727-10.666l113.292,79.097c-1.629,6.017-2.514,12.34-2.514,18.872c0,39.79,32.257,72.047,72.047,72.047
                          c39.79,0,72.047-32.257,72.047-72.047C411.635,346.787,379.378,314.529,339.588,314.529z"/>
          </g>
  </g>
  </svg>
                  Export
                </button>

                <!-- Overlay Mask toggle if we have a mask -->
                ${this.maskImage
            ? html`
                      <label class="inline-flex items-center ml-4">
                        <input
                          type="checkbox"
                          class="h-4 w-4"
                          .checked=${this.overlayMask}
                          @change=${this.toggleMaskOverlay}
                        />
                        <span class="ml-2 text-sm">Overlay Mask</span>
                      </label>
                    `
            : ''}
              </div>
            </div>
          `
        : ''}

        ${this.renderCharacterGallery()}
    `;
  }

  renderColorModal() {
    if (this.colorModalOpenForGarmentId == null) return null;

    const garmentId = this.colorModalOpenForGarmentId;
    const filtered = filterColors(this.colorSearch);
    return html`
      <div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" @click=${this.closeColorModal}>
        <div
          class="bg-gray-800 p-4 rounded w-[320px] max-h-[80vh] overflow-y-auto"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <h2 class="text-lg font-bold mb-2">Pick a Color</h2>
          <input
            type="text"
            class="w-full p-2 text-black mb-2"
            placeholder="Search colors..."
            .value=${this.colorSearch}
            @input=${(e: any) => this.colorSearch = e.target.value}
          />
          <!-- Grid of colors -->
          <div class="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
            ${filtered.map((colorName) => html`
              <div
                class="border border-gray-500 rounded p-1 text-center cursor-pointer hover:bg-gray-700 text-sm"
                @click=${() => this.setGarmentColor(garmentId, colorName)}
              >
                ${colorName}
              </div>
            `)}
          </div>

          <hr class="my-2" />

          <div class="text-sm text-gray-400 mb-1">
            Custom color must be specified as a name or textual description (Hex/RGB values will not work)
          </div>
          <div class="flex gap-2">
            <input
              type="text"
              id="customColorInput"
              class="w-full p-2 text-black"
              placeholder="e.g. 'platinum'"
            />
            <button
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
              @click=${() => {
        const input = this.renderRoot?.querySelector('#customColorInput') as HTMLInputElement;
        if (!input) return;
        const c = input.value.trim();
        if (c) this.setGarmentColor(garmentId, c);
      }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // -------------- SELECTED GARMENTS --------------
  renderSelectedGarments() {
    if (this.selectedGarments.length === 0) {
      return html`
      <span class="text-gray-500 text-sm">None selected</span>
    `;
    }
    return html`
    ${this.selectedGarments.map((g) => {
      const colorName = g.color || '';
      const style = colorName
        ? `background: ${colorName};`
        : `
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 5px,
              rgba(255,255,255,0.5) 5px,
              rgba(255,255,255,0.5) 10px
            );
          `;
      const segLabel = g.outfitSegment.toUpperCase(); // e.g. "TOP", "BOTTOM", "FULL"

      return html`
        <div
          class="flex items-center px-2 py-1 bg-pink-600 rounded text-white text-sm gap-2"
        >
          <!-- Color swatch -->
          <div
            class="w-5 h-5 border border-white cursor-pointer"
            style="${style}"
            @click=${(e) => {
          e.stopPropagation();
          this.openColorModal(g.id);
        }}
            title="Click to pick color"
          ></div>

          <!-- Garment label + segment -->
          <div class="cursor-pointer flex items-center"
               @click=${() => this.toggleGarment(g)}>
            <!-- Show e.g. "bralette (TOP)" -->
            ${g.label} <span class="ml-2 text-xs text-pink-200">(${segLabel})</span>
            <span class="ml-2 text-xs text-pink-200">✕</span>
          </div>
        </div>
      `;
    })}
  `;
  }

  renderCustomGarmentListItems() {
    if (this.filteredCustomGarments.length === 0) {
      return html`<div class="text-gray-400 text-sm">No custom garments found.</div>`;
    }
    return this.filteredCustomGarments.map((g) => {
      const selected = this.isGarmentSelected(g);
      return html`
        <div
          class="
            p-2 my-1 flex justify-between items-center
            ${selected ? 'border-2 border-pink-600 bg-gray-800' : 'bg-gray-800 hover:bg-gray-700'}
          "
          @click=${() => this.toggleGarment({ ...g, category: 'custom' })}
        >
          <div class="font-semibold">${g.promptFragment}</div>

          <!-- 3-dot button (stopPropagation so it doesn't toggle garment) -->
          <div class="relative" @click=${(e) => e.stopPropagation()}>
            <button
              class="p-1 rounded hover:bg-gray-600"
              @click=${(e: MouseEvent) => this.toggleContextMenuFor(g.id, e)}
            >
              ⋮
            </button>
            ${this.contextMenuOpenForGarmentId === g.id
          ? html`
                  <div class="absolute left-0 mt-1 w-24 bg-base-100 shadow rounded p-1 text-sm z-50">
                    <button
                      class="block w-full text-left px-2 py-1 hover:bg-base-300"
                      @click=${() => this.openEditCustomGarment(g)}
                    >
                      Edit
                    </button>
                    <button
                      class="block w-full text-left px-2 py-1 hover:bg-base-300"
                      @click=${() => this.deleteCustomGarment(g)}
                    >
                      Delete
                    </button>
                  </div>
                `
          : ''}
          </div>
        </div>
      `;
    });
  }

  renderEditForm() {
    if (!this.showEditForm || !this.editingCustomGarment) return null;
    const g = this.editingCustomGarment;
    return html`
      <div class="p-2 bg-gray-700 rounded mb-2 mt-2 border border-gray-500">
        <h3 class="font-semibold mb-2">Edit Custom Garment</h3>
        <label class="block mb-1 text-sm font-semibold">Label</label>
        <input
          type="text"
          class="mb-2 w-full p-2 rounded bg-gray-800 border border-gray-600"
          .value=${g.label}
          @change=${(e) => (this.editingCustomGarment.label = e.target.value)}
        />
        <label class="block mb-1 text-sm font-semibold">Prompt Fragment</label>
        <input
          type="text"
          class="mb-2 w-full p-2 rounded bg-gray-800 border border-gray-600"
          .value=${g.promptFragment}
          @change=${(e) => (this.editingCustomGarment.promptFragment = e.target.value)}
        />
        <label>
          Outfit Segment
          <select
            class="select"
            .value=${g.outfitSegment}
            @change=${(e) => (this.editingCustomGarment.outfitSegment = e.target.value)}
          >
            <option .value=${GarmentOutfitSegment.TOP} ?selected="${GarmentOutfitSegment.TOP === this.editingCustomGarment.outfitSegment}">Top</option>
            <option .value=${GarmentOutfitSegment.BOTTOM} ?selected="${GarmentOutfitSegment.BOTTOM === this.editingCustomGarment.outfitSegment}">Bottom</option>
            <option .value=${GarmentOutfitSegment.FULL} ?selected="${GarmentOutfitSegment.FULL === this.editingCustomGarment.outfitSegment}">Full</option>
          </select>
        </label>

        <div class="mt-3 flex gap-2">
          <button class="px-3 py-1 rounded border border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white"
            @click=${this.updateCustomGarment}>
            Save
          </button>
          <button class="px-3 py-1 rounded bg-red-600 hover:bg-red-700"
            @click=${this.cancelEditCustomGarment}>
            Cancel
          </button>
        </div>
      </div>
    `;
  }

  // Replaces the old inline custom garment list with a new function call
  renderCustomSection() {
    return html`
      <div class="my-2 max-w-full">
        <!-- Button to add new custom garment -->
        <button class="btn btn-sm btn-outline btn-info mb-2" @click=${this.toggleCustomForm}>
          + Custom Garment
        </button>

        <!-- The "Create" form -->
        ${this.showCustomForm
        ? html`
              <div class="p-2 bg-gray-700 rounded mb-2">
                <label class="block mb-1 text-sm font-semibold">Prompt Fragment</label>
                <input
                  type="text"
                  class="mb-2 w-full p-2 rounded bg-gray-800 border border-gray-600"
                  .value=${this.newGarmentPrompt}
                  @change=${(e) => (this.newGarmentPrompt = e.target.value)}
                />
                <label>
                  Outfit Segment
                  <select
                    class="select"
                    @change=${(e) => (this.newGarmentSegment = e.target.value)}
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="full" selected>Full</option>
                  </select>
                </label>
                <!-- Prompting Guide -->
                <div class="flex flex-col gap-2 p-2 m-2 rounded border border-secondary">
                  <strong>Prompting Guide:</strong>
                  <p>
                    Be concise. If result isn’t what you want, try synonyms or alternate
                    garment names (e.g., "shirt", "crop top", "tank top", etc).
                  </p>
                </div>

                <button
                  class="px-3 py-1 rounded border border-pink-600 text-pink-600 bg-transparent
                         hover:bg-pink-600 hover:text-white"
                  @click=${this.createCustomGarment}
                >
                  Save
                </button>
                <button
                  class="px-3 py-1 rounded bg-red-600 hover:bg-red-700 ml-2"
                  @click=${this.toggleCustomForm}
                >
                  Cancel
                </button>
              </div>
            `
        : ''}

        <!-- The Edit Form (if user clicked "Edit" on a garment) -->
        ${this.renderEditForm()}

        <!-- List all custom garments -->
        ${this.renderCustomGarmentListItems()}
      </div>
    `;
  }

  // Renders the entire garment section (includes the custom block + categories, etc.)
  renderGarmentsSection() {
    const anyMatches = this.foundAnyMatches;
    const highlight = this.tourStep === 2 ? 'animate-pulseShadow' : '';
    const highlightButton = this.tourStep === 3 ? 'animate-pulseShadow' : '';

    return html`
      <div class="card bordered bg-base-200 shadow">
        <div class="flex flex-col h-full card-body p-2">
          <h2 class="text-xl font-semibold mb-4 card-title">🛍️ Garments</h2>

          <div class="overflow-y-auto p-2 ${highlight}">
            <h3 class="text-sm font-semibold mb-1">Selected Garments:</h3>
            <div class="flex flex-wrap gap-2 mb-4">
              ${this.renderSelectedGarments()}
            </div>

            <div class="divider"></div>

            <div class="form-control mb-4">
              <input
                type="text"
                placeholder="Search all garments..."
                class="input input-bordered w-full"
                .value=${this.searchTerm}
                @input=${(e: any) => {
        const search = e.target.value
        this.searchTerm = search
        ModelMeAIAnalytics.getPlausible()?.trackEvent('garment_search', { props: { search } })
      }}
              />
            </div>

            ${!anyMatches && this.searchTerm
        ? html`
                  <div class="text-sm text-gray-400 mb-2">
                    No matching garments found.
                    <a class="btn btn-sm btn-outline btn-info ml-2" @click=${() => (this.openCustom = true)}>
                      Make Custom Garment
                    </a>
                  </div>
                `
        : ''}

            <div class="collapse collapse-arrow bg-base-100 mb-2">
              <input
                type="checkbox"
                class="peer"
                ?checked=${this.openCustom}
                @change=${() => (this.openCustom = !this.openCustom)}
              />
              <div class="collapse-title font-semibold">
                Custom Garments
              </div>
              <div class="collapse-content">
                ${this.openCustom ? this.renderCustomSection() : ''}
              </div>
            </div>

            <!-- Garment categories -->
            <div class="menu w-full p-0">
              <h4 class="font-bold py-4">
                Premade Garments
              </h4>
              ${Object.entries(this.garmentsFilteredBySearch).map(([cat, items]) => {
          const isOpen = this.openCategories[cat] || this.searchTerm.length > 2;
          const filteredItems = items.filter(i =>
            isGarmentEligibleForSelection(i, this.selectedGarments)
          );

          if (filteredItems.length === 0) {
            return '';
          }
          return html`
                  <div class="collapse collapse-arrow bg-base-300 my-2">
                    <input
                      type="checkbox"
                      class="peer"
                      .checked=${isOpen}
                      @change=${() => this.toggleCategory(cat)}
                    />
                    <div class="collapse-title font-semibold">
                      ${formatGarmentCategory(cat)}
                    </div>
                    <div class="collapse-content ml-2">
                      ${filteredItems.map((g) => {
            const selected = this.isGarmentSelected(g);
            return html`
                          <div
                            class="my-1 p-2 rounded cursor-pointer
                              ${selected
                ? 'border border-pink-600 bg-base-200'
                : 'hover:bg-base-200'}"
                            @click=${() => this.toggleGarment(g)}
                          >
                            ${formatGarmentLabel(g.label)}
                          </div>
                        `;
          })}
                    </div>
                  </div>
                `;
        })}
            </div>
          </div>

          <div class="divider"></div>

          <div class="p-2 card-actions ${highlightButton}">
            ${this.renderAIOptions()}

            <button class="btn btn-primary text-white w-full mt-3" @click=${this.handleGenerateImage}>
              ${this.loading ? html`<span class="loading loading-dots loading-md"></span>` : ''}
              🚀 Generate Image
            </button>
          </div>

          ${this.renderColorModal()}
        </div>
      </div>
    `;
  }

  renderNoPlanModal() {
    if (!this.showNoPlanModal) return null;
    return html`
      <div class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div class="bg-gray-800 p-6 rounded w-80">
          <h2 class="text-xl font-bold mb-2">No Plan</h2>
          <p class="mb-4">You need to select a plan to generate images.</p>
          <button
            class="px-4 py-2 border border-pink-600 text-pink-600 bg-transparent hover:bg-pink-600 hover:text-white rounded"
            @click=${() => {
        ModelMeAIAnalytics.getPlausible()?.trackEvent('click_no_plan_modal_view_plans')
        this.showNoPlanModal = false;
        window.history.pushState({}, '', '/my-plan');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }}
          >
            View Plans
          </button>
          <button
            class="px-4 py-2 bg-red-600 hover:bg-red-700 rounded ml-2"
            @click=${() => {
        ModelMeAIAnalytics.getPlausible()?.trackEvent('click_no_plan_modal_cancel')
        this.showNoPlanModal = false
      }}
          >
            Cancel
          </button>
        </div>
      </div>
    `;
  }

  renderUpgradeModal() {
    if (!this.showUpgradeModal) return null;
    return html`
      <div class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div class="bg-gray-800 p-6 rounded w-80">
          <h2 class="text-xl font-bold mb-2">Limit Reached</h2>
          <p class="mb-4">You’ve hit your monthly image limit. Upgrade to continue.</p>
          <button
            class="px-4 py-2 border border-pink-600 text-pink-600 bg-transparent hover:bg-pink-600 hover:text-white rounded"
            @click=${() => {
        this.showUpgradeModal = false;
        window.history.pushState({}, '', '/my-plan');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }}
          >
            Upgrade Plan
          </button>
          <button
            class="px-4 py-2 bg-red-600 hover:bg-red-700 rounded ml-2"
            @click=${() => (this.showUpgradeModal = false)}
          >
            Cancel
          </button>
        </div>
      </div>
    `;
  }

  renderAIOptions() {
    return html`
      <div class="flex flex-col gap-3">
        <div class="collapse bg-base-100 border border-gray-300">
          <input type="checkbox" />
          <div class="collapse-title text-lg font-semibold flex justify-between" style="padding-inline-end: 1rem;"><span>🤖 AI Options</span> <span>▼</span></div>
          <div class="collapse-content text-sm space-y-4">
            <!-- Positive Prompt -->
            <div class="form-control">
              <label class="label text-sm text-green-400">Positive Prompt</label>
              <textarea
                class="textarea textarea-success min-h-4 h-8"
                placeholder="e.g. bodycon, curvy"
                .value=${this.positivePrompt}
                @input=${(e: any) => {
        const val = e.target.value
        this.positivePrompt = val
        ModelMeAIAnalytics.getPlausible()?.trackEvent('positive_prompt', { props: { val } })
      }}
              ></textarea>
              <label class="label text-xs text-gray-500">
                <span style="text-wrap: auto">Optional. Use keywords to describe what style/elements to emphasize in the result.</span>
              </label>
            </div>

            <!-- Negative Prompt -->
            <div class="form-control">
              <label class="label text-sm text-red-400">Negative Prompt</label>
              <textarea
                class="textarea textarea-error min-h-4 h-8"
                placeholder="e.g. dull color, long sleeves"
                .value=${this.negativePrompt}
                @input=${(e: any) => {
        const val = e.target.value
        this.negativePrompt = val
        ModelMeAIAnalytics.getPlausible()?.trackEvent('negative_prompt', { props: { val } })
      }}
              ></textarea>
              <label class="label text-xs text-gray-500">
                <span style="text-wrap: auto">Optional. Use keywords to describe what to exclude from the result.</span>
              </label>
            </div>

            <!-- Upscale -->
            <div class="hidden">
              <label class="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary mr-2"
                  .checked=${this.upscale}
                  @change=${(e) => (this.upscale = e.target.checked)}
                />
                <span>Upscale Image</span>
              </label>
              <p class="text-xs text-gray-400 block">
                If selected, the final image will be upscaled for higher resolution.  This can significantly alter the image.
              </p>
            </div>

            <div class="">
              <label class="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary mr-2"
                  .checked=${this.isNude}
                  @change=${(e) => (this.isNude = e.target.checked)}
                />
                <span>Nudify</span>
              </label>
              <p class="text-xs text-gray-400 block">
                If selected, the final image will be 'Nudified'.
              </p>
            </div>

            <!-- Strength Slider -->
            <div class="mb-3 hidden">
              <label class="block font-semibold mb-1">Strength</label>
              <input
                type="range"
                min="1"
                max="100"
                step="5"
                .value=${this.strength}
                @input=${(e) => (this.strength = Number(e.target.value))}
              />
              <p class="text-sm text-gray-400">
                Set how much to modify original image. Stronger is better for full outfit changes. If unsure, leave unchanged.
              </p>
            </div>

          </div>
        </div>
      </div>
    `;
  }

  renderBetaAlert() {
    return html`
      <div class="alert alert-soft alert-warning shadow-lg my-4 max-w-xl mx-auto flex justify-center">
        <div class="flex gap-4 w-[85%]">
          <svg
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="fill-white w-24 lg:w-12"
          >
            <rect width="48" height="48" fill="white" fill-opacity="0.01" />
            <path d="M12 4H36" stroke="#000" stroke-width="4" stroke-linecap="round" />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M18.019 4V15.0386L6.27443 39.3014C5.48692 40.9283 6.16737 42.8855 7.79427 43.673C8.23882 43.8882 8.7263 44 9.22019 44H38.7875C40.595 44 42.0602 42.5347 42.0602 40.7273C42.0602 40.2348 41.9491 39.7488 41.7351 39.3052L30.0282 15.0386V4H18.019Z"
              stroke="#000"
              stroke-width="4"
              stroke-linejoin="round"
            />
            <path
              d="M10.9604 29.9998C13.1241 31.3401 15.2893 32.0103 17.4559 32.0103C19.6226 32.0103 21.7908 31.3401 23.9605 29.9998C26.1088 28.6735 28.2664 28.0103 30.433 28.0103C32.5997 28.0103 34.7755 28.6735 36.9604 29.9998"
              stroke="#000"
              stroke-width="4"
              stroke-linecap="round"
            />
          </svg>
          <div class="">
            <strong class="w-full">Beta Version:</strong>
            <p class="text-xs ">
              ModelMeAI uses cutting-edge AI tech that may produce unexpected
              results. Downvote undesired generated images to help reduce errors.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  renderStepsComponent() {
    // We have 3 steps: 1) Model, 2) Garments, 3) Generate
    // If tourStep=1 => step1 is "active", steps 2&3 are incomplete
    // If tourStep=2 => step1 is "done", step2 is "active", step3 incomplete
    // If tourStep=3 => step1&2 are "done", step3 is "active"
    // If tourStep=0 => all are "done"
    const step1Class = this.getStepClass(1);
    const step2Class = this.getStepClass(2);
    const step3Class = this.getStepClass(3);

    return html`
      <!-- DaisyUI steps (horizontal) -->
      <ul class="steps w-full">
        <li class="step ${step1Class}">⭐ Select Model</li>
        <li class="step ${step2Class}">🛍️ Select Garments</li>
        <li class="step ${step3Class}">🚀 Generate</li>
      </ul>
    `;
  }

  /**
   * getStepClass(stepNumber) returns a class: 
   * "step-primary" or "step-info" or "step-accent" or "step-neutral" or "step-success" 
   * according to whether the step is done, current, or to-do.
   */
  getStepClass(stepNumber: number) {
    if (this.tourStep === 0) {
      // 0 => all done => all steps are "step-success"
      return "step-primary";
    }
    // if this.tourStep < stepNumber => step is incomplete
    // if this.tourStep == stepNumber => current step
    // if this.tourStep > stepNumber => done
    if (this.tourStep > stepNumber) {
      // step is completed
      return "step-primary";
    } else if (this.tourStep === stepNumber) {
      // current step
      return "step-primary";
    } else {
      // step not reached yet => "step"
      return "text-[rgba(255,255,255,0.5)]";
    }
  }

  // Example: incorporate the steps into your "Get Started" or top banner
  renderGetStartedSteps() {
    return html`
      <div class="card bg-base-200 shadow-lg p-4 mb-4 text-white">
        ${this.renderStepsComponent()}
      </div>
    `;
  }

  renderUserGuide() {
    return html`
      <dialog id="user_guide_modal" class="modal">
        <div class="modal-box p-2 mt-2 border border-gray-600 bg-base-200 rounded text-sm text-gray-300">
          <h3 class="mb-2 ">Thanks For Rating The Result!</h3>
          <h4 class="text-lg font-bold mb-2">Learn More About Advanced Prompting ModelMeAI</h4>
          <p class="mb-2">
            ModelMeAI's prompt system helps you refine how your outfits are generated.  
            Use <strong class="text-green-400">positive prompts</strong> to specify details you want emphasized,  
            and <strong class="text-red-400">negative prompts</strong> to exclude certain elements or styles.  
            <!--Adjust the <strong>Strength</strong> slider to control how strongly your prompt overrides the original image.-->
          </p>
          <div class="mb-2">
            <strong>Positive Prompt Examples:</strong>
            <ul class="list-disc list-inside ml-4">
              <li>Clothing coverage: "bare legs"</li>
              <li>Clothing colors: "{color} primary color, {color} secondary color", etc</li>
              <li>Clothing fit: "tight fit, loose fit, casual fit", etc</li>
              <li>Clothing style: "low rise, high rise, high waisted", etc</li>
              <li>Pose: "front view, back view", etc</li>
            </ul>
          </div>
          <div class="mb-2">
            <strong>Negative Prompt Examples:</strong>
            <ul class="list-disc list-inside ml-4">
              <li>Overexposure: "bare {body part}, naked {body part}"</li>
              <li>Color bias: "{color} clothing"</li>
            </ul>
          </div>
          <!--<div>
            <strong>Strength Slider Tips:</strong>
            <p class="mt-1">
              A higher strength applies more substantial changes compared to your source image.  
              A lower strength keeps more of the original photo’s details.
            </p>
          </div>-->
          <div>
            <strong>Direct Support</strong>
            <p class="mt-1">
              We stand behind our product. If the above steps do not improve the results, please contact <a href="mailto:support@modelmeai.com" class="font-bold underline">support@modelmeai.com</a>.
            </p>
          </div>
          <div class="modal-action">
            <form method="dialog">
              <button class="btn">Close</button>
            </form>
          </div>
        </div>
    </dialog>
    `;
  }

  render() {
    const previewSection = html`
             <!-- Preview -->
            <div class="w-full md:w-1/2 card card-border bg-base-200 ">
              ${this.renderPreviewSection()}
            </div>
    `

    return html`
      <auth-prompt
        ?isOpen=${this.showAuthPrompt}
        @auth-success=${() => {
        this.showAuthPrompt = false;
        this.isSignedIn = true;
      }}
        @cancel=${() => {
        this.showAuthPrompt = false;
      }}
      ></auth-prompt>
      <modelmeai-welcome></modelmeai-welcome>

      ${this.renderNoPlanModal()}
      ${this.renderUpgradeModal()}
      <!-- The new Model Modal -->
      ${this.renderModelModal()}

      ${this.renderUserGuide()}

      <div class="min-h-[80vh]">
        <div class="divider divider-accent font-bold"><span>ModelMe<span class="text-primary">AI</span></span></div>
        ${this.renderGetStartedSteps()}

        <!-- Main Layout -->
        <div class="text-base-content ">
          <div class="flex flex-col md:flex-row px-2 gap-2">
            ${isMobile ? previewSection : ''}

            <!-- Garments -->
            <div class="w-full md:w-1/4 space-y-4 ">
              <!--${/*this.renderModelSelector()*/0}-->
              <div style="400px; overflow-y: auto;">
                ${this.renderGarmentsSection()}
              </div>
            </div>

            ${isMobile ? '' : previewSection}

            <!-- Generations -->
            <div class="w-full md:w-1/4 overflow-y-auto ">
              <div class="card card-border bg-base-200 ">
                <div class="card-body p-2">
                  ${this.renderGenerationsSection()}
                </div>
              </div>
            </div>
          </div>

          <div class="p-4">
            ${this.renderBetaAlert()}
          </div>

        </div>
      </div>
      <div class="divider divider-accent"></div>
    `;
  }
}

customElements.define('dressing-room', DressingRoom);

